import { compareTreeOrder } from "./entityTree";
/** Pure helpers for tasks, dependencies and milestones (mirrors crates/tern-domain). */
import type { Issue, Milestone, ProjectData, Task } from "./store";
import { liveIssues, liveMilestones, liveTasks } from "./store";

export { TASK_STATUSES, TASK_STATUS_COLOR, type TaskStatus } from "./workflow";
import { isFinalStatus, progressCredit } from "./workflow";

export const HEALTH_COLOR: Record<string, string> = { on_track: "#4cb782", at_risk: "#f2c94c", off_track: "#eb5757" };

export type Progress = { ratio: number; total: number; completed: number; started: number } | null;

/** docs/features/tasks.md §3 — completed 100%, in-progress/pending/review 25%, canceled excluded. */
export function progressOf(d: ProjectData, issues: Issue[]): Progress {
  let num = 0;
  let den = 0;
  let total = 0;
  let completed = 0;
  let started = 0;
  for (const i of issues) {
    const type = d.states[i.state_id]?.type;
    if (type === "canceled") continue;
    const w = d.project.estimate_enabled ? (i.estimate && i.estimate > 0 ? i.estimate : 1) : 1;
    den += w;
    total += 1;
    if (type === "completed") {
      num += w;
      completed += 1;
    } else if (progressCredit(type) > 0) {
      num += w * progressCredit(type);
      started += 1;
    }
  }
  return den === 0 ? null : { ratio: Math.min(1, num / den), total, completed, started };
}

/** Issues belonging to a task, including its sub-tasks' issues (recursive). */
export function issuesOfTaskTree(d: ProjectData, taskId: string): Issue[] {
  const ids = new Set(descendants(d, taskId).map((t) => t.id));
  ids.add(taskId);
  return liveIssues(d).filter((i) => i.task_id && ids.has(i.task_id));
}

export function issuesOfTask(d: ProjectData, taskId: string): Issue[] {
  return liveIssues(d).filter((i) => i.task_id === taskId);
}

export function taskProgress(d: ProjectData, task: Task): Progress {
  return progressOf(d, issuesOfTaskTree(d, task.id));
}

export function children(d: ProjectData, taskId: string): Task[] {
  return liveTasks(d)
    .filter((t) => t.parent_id === taskId)
    .sort(compareTreeOrder);
}

export function descendants(d: ProjectData, taskId: string): Task[] {
  const out: Task[] = [];
  const stack = [taskId];
  const seen = new Set<string>();
  while (stack.length) {
    const id = stack.pop()!;
    for (const c of children(d, id)) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
      stack.push(c.id);
    }
  }
  return out;
}

export function ancestors(d: ProjectData, task: Task): Task[] {
  const out: Task[] = [];
  let cur = task.parent_id ? d.tasks[task.parent_id] : undefined;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = cur.parent_id ? d.tasks[cur.parent_id] : undefined;
  }
  return out;
}

/** Tasks that must finish before `taskId` (선행). */
export function predecessors(d: ProjectData, taskId: string): { dep: (typeof d.dependencies)[string]; task: Task }[] {
  return Object.values(d.dependencies)
    .filter((dep) => dep.successor_id === taskId)
    .map((dep) => ({ dep, task: d.tasks[dep.predecessor_id]! }))
    .filter((x) => x.task && !x.task.deleted_at);
}

/** Tasks waiting on `taskId` (후행). */
export function successors(d: ProjectData, taskId: string): { dep: (typeof d.dependencies)[string]; task: Task }[] {
  return Object.values(d.dependencies)
    .filter((dep) => dep.predecessor_id === taskId)
    .map((dep) => ({ dep, task: d.tasks[dep.successor_id]! }))
    .filter((x) => x.task && !x.task.deleted_at);
}

export const isDone = (t: Task) => isFinalStatus(t.status);

/** Blocked = has an unfinished predecessor. */
export function isBlocked(d: ProjectData, taskId: string): boolean {
  return predecessors(d, taskId).some((p) => !isDone(p.task));
}

/** Depth-first tree of root tasks (no parent, or parent missing) with nesting. */
export type TaskNode = { task: Task; depth: number; children: TaskNode[] };
export function taskTree(d: ProjectData, tasks: Task[]): TaskNode[] {
  const byId = new Set(tasks.map((t) => t.id));
  const build = (t: Task, depth: number): TaskNode => ({
    task: t,
    depth,
    children: tasks
      .filter((c) => c.parent_id === t.id)
      .sort(compareTreeOrder)
      .map((c) => build(c, depth + 1)),
  });
  return tasks
    .filter((t) => !t.parent_id || !byId.has(t.parent_id))
    .sort(compareTreeOrder)
    .map((t) => build(t, 0));
}

export function flattenTree(nodes: TaskNode[], collapsed: Set<string>): TaskNode[] {
  const out: TaskNode[] = [];
  const walk = (n: TaskNode) => {
    out.push(n);
    if (!collapsed.has(n.task.id)) n.children.forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}

// ---- Milestones -------------------------------------------------------------

export type MilestoneStatus = "upcoming" | "active" | "overdue" | "completed";

export function milestoneStatus(m: Milestone, today = todayISO()): MilestoneStatus {
  if (m.completed_at) return "completed";
  if (today > m.target_date) return "overdue";
  if (m.start_date && today < m.start_date) return "upcoming";
  return "active";
}

export function milestoneTasks(d: ProjectData, milestoneId: string): Task[] {
  return liveTasks(d).filter((t) => t.milestone_id === milestoneId);
}

export function milestoneProgress(d: ProjectData, milestoneId: string): Progress {
  const taskIds = new Set(milestoneTasks(d, milestoneId).flatMap((t) => [t.id, ...descendants(d, t.id).map((x) => x.id)]));
  return progressOf(d, liveIssues(d).filter((i) => i.task_id && taskIds.has(i.task_id)));
}

/** A milestone is at risk when one of its tasks targets a date after the milestone. */
export function milestoneAtRisk(d: ProjectData, m: Milestone): boolean {
  return milestoneTasks(d, m.id).some((t) => !isDone(t) && t.target_date && t.target_date > m.target_date);
}

export function nextMilestone(d: ProjectData): Milestone | undefined {
  const today = todayISO();
  return liveMilestones(d).find((m) => !m.completed_at && m.target_date >= today) ?? liveMilestones(d).find((m) => !m.completed_at);
}

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86_400_000);
}

export function addDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ---- Dependency graph layout (layered, longest-path) --------------------------

export type GraphNode = { task: Task; col: number; row: number };
export type GraphEdge = { from: string; to: string; kind: "dep" | "parent" };

export function layoutGraph(d: ProjectData, tasks: Task[]): { nodes: GraphNode[]; edges: GraphEdge[]; cols: number; rows: number } {
  const ids = new Set(tasks.map((t) => t.id));
  const deps = Object.values(d.dependencies).filter((x) => ids.has(x.predecessor_id) && ids.has(x.successor_id));
  const preds = new Map<string, string[]>();
  for (const t of tasks) preds.set(t.id, []);
  for (const x of deps) preds.get(x.successor_id)!.push(x.predecessor_id);
  // Column = longest path from a source (memoised; graph is acyclic by construction).
  const col = new Map<string, number>();
  const depth = (id: string, guard = 0): number => {
    if (col.has(id)) return col.get(id)!;
    if (guard > 500) return 0;
    const c = Math.max(-1, ...(preds.get(id) ?? []).map((p) => depth(p, guard + 1))) + 1;
    col.set(id, c);
    return c;
  };
  tasks.forEach((t) => depth(t.id));
  // Keep sub-tasks near their parent: sort by (col, parent chain, sort_order).
  const key = (t: Task) => `${ancestors(d, t).map((a) => a.sort_order).join("/")}/${t.sort_order}`;
  const byCol = new Map<number, Task[]>();
  for (const t of tasks) (byCol.get(col.get(t.id)!) ?? byCol.set(col.get(t.id)!, []).get(col.get(t.id)!)!).push(t);
  const nodes: GraphNode[] = [];
  let rows = 0;
  for (const [c, list] of byCol) {
    list.sort((a, b) => key(a).localeCompare(key(b)));
    list.forEach((t, r) => nodes.push({ task: t, col: c, row: r }));
    rows = Math.max(rows, list.length);
  }
  const edges: GraphEdge[] = [
    ...deps.map((x) => ({ from: x.predecessor_id, to: x.successor_id, kind: "dep" as const })),
    ...tasks.filter((t) => t.parent_id && ids.has(t.parent_id)).map((t) => ({ from: t.parent_id!, to: t.id, kind: "parent" as const })),
  ];
  return { nodes, edges, cols: byCol.size, rows };
}
